import { useRef, useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useAutoPlay } from "./useAutoPlay"
import { Icon, IconColor } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import { resetAudioPlayback } from "@/store/audioPlaybackSlice"
import "./index.scss"

interface AudioPlayerProps {
   src: string
   type: string
   autoPlay?: boolean
   stopOthers?: boolean
   loop?: boolean
}

let currentlyPlaying: {
   audio: HTMLAudioElement
   stop: () => void
} | null = null

export function AudioPlayer({
   src,
   type,
   autoPlay = true,
   stopOthers = false,
   loop = false,
}: AudioPlayerProps) {
   const audioRef = useRef<HTMLAudioElement | null>(null)
   const dispatch = useDispatch()
   const [isPlaying, setIsPlaying] = useState(false)
   useAutoPlay(`/assets/audio/${src}`, loop, autoPlay)

   useEffect(() => {
      const handleEnded = () => {
         setIsPlaying(false)
         dispatch(resetAudioPlayback())
         if (stopOthers && currentlyPlaying?.audio === audioRef.current) {
            currentlyPlaying = null
         }
      }

      const currentAudio = audioRef.current
      if (currentAudio) {
         currentAudio.loop = loop
         currentAudio.addEventListener("ended", handleEnded)
      }

      return () => {
         if (currentAudio) {
            currentAudio.removeEventListener("ended", handleEnded)
         }
      }
   }, [stopOthers, loop, dispatch])

   const stopThis = () => {
      if (audioRef.current) {
         audioRef.current.pause()
         audioRef.current.currentTime = 0
         setIsPlaying(false)
      }
   }

   const handleToggle = () => {
      if (!audioRef.current) return

      if (
         stopOthers &&
         currentlyPlaying &&
         currentlyPlaying.audio !== audioRef.current
      ) {
         currentlyPlaying.stop()
      }

      if (audioRef.current.paused) {
         audioRef.current.play().then(() => {
            if (stopOthers) {
               currentlyPlaying = {
                  audio: audioRef.current!,
                  stop: stopThis,
               }
            }
            setIsPlaying(true)
         })
      } else {
         stopThis()
         if (stopOthers && currentlyPlaying?.audio === audioRef.current) {
            currentlyPlaying = null
         }
      }
   }

   return (
      <>
         {stopOthers && (
            <>
               {!isPlaying && (
                  <Icon
                     size="16"
                     color={IconColor.HONOLULU_BLUE}
                     name="play"
                     onClick={handleToggle}
                     data-testid={TestId.AUDIO_PLAYER_PLAY_BUTTON}
                  />
               )}

               {isPlaying && (
                  <span
                     className="audio-player__stop"
                     data-testid={TestId.AUDIO_PLAYER_STOP_BUTTON}
                     onClick={() => {
                        stopThis()
                        if (
                           stopOthers &&
                           currentlyPlaying?.audio === audioRef.current
                        ) {
                           currentlyPlaying = null
                        }
                     }}
                  >
                     Stop
                  </span>
               )}
            </>
         )}
         {!autoPlay && (
            <audio
               ref={audioRef}
               loop={loop}
               data-testid={TestId.AUDIO_PLAYER_AUDIO}
            >
               <source
                  src={`/assets/audio/${src}`}
                  type={type}
                  data-testid={TestId.AUDIO_PLAYER_AUDIO_SOURCE}
               />
               Your browser does not support the audio element.
            </audio>
         )}
      </>
   )
}
